import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useSettings } from '../../context/SettingsContext';
import { useUI } from '../../context/UIContext';
import { pct } from '../../utils/format';
import Image from '../common/Image';
import Price from './Price';
import Stars from '../common/Stars';
import { IconHeart, IconHeartFilled, IconEye } from '../common/Icons';

// Product card replicating the reference "sf__pcard-style-1": hover image swap, action icons, quick add, option preview.
export default function ProductCard({ product, className = '', listView = false }) {
  const { add, toggleWish, inWishlist } = useCart();
  const { settings } = useSettings();
  const { toast } = useUI();
  const navigate = useNavigate();
  const p = product;
  const main = p.images?.[0]?.url;
  const hover = p.images?.[1]?.url;
  const onSale = p.compareAtPrice && p.compareAtPrice > p.price;
  const hasOptions = (p.options && p.options.length > 0) || (p.variants && p.variants.length > 1);
  const wished = inWishlist(p._id);
  const firstOption = p.options?.[0];
  const optionValues = firstOption ? firstOption.values.slice(0, 2) : [];
  const rest = firstOption ? firstOption.values.length - optionValues.length : 0;
  const soldOut = p.stock <= 0 && (!p.variants || !p.variants.some((v) => v.stock > 0));

  const quickAdd = () => {
    if (hasOptions) return navigate(`/products/${p.slug}`);
    add({ productId: p._id, title: p.title, slug: p.slug, image: main, price: p.price, compareAtPrice: p.compareAtPrice, quantity: 1 });
    return toast('Added to cart');
  };

  return (
    <div className={`sf__pcard ${onSale ? 'sf__pcard--onsale' : ''} cursor-pointer sf-prod__block sf__pcard-style-1 ${className}`}>
      <div className="sf__pcard-image">
        <div className="overflow-hidden cursor-pointer relative sf__image-box">
          <div className="flex justify-center items-center">
            <Link to={`/products/${p.slug}`} className="block w-full">
              <div className="spc__main-img">
                <Image src={main} alt={p.images?.[0]?.alt || p.title} ratio="1/1" imgClassName="se-out w-full h-full" />
              </div>
              {hover && (
                <div className="spc__hover-img">
                  <Image src={hover} alt={p.images?.[1]?.alt || p.title} ratio="1/1" imgClassName="se-out w-full h-full" />
                </div>
              )}
            </Link>
          </div>
          <div className="eg-badges absolute top-3 left-3 flex flex-col gap-1 z-10">
            {onSale && <span className="eg-badge eg-badge--sale">-{pct(p.price, p.compareAtPrice)}%</span>}
            {p.badge && <span className="eg-badge">{p.badge}</span>}
            {soldOut && <span className="eg-badge eg-badge--soldout">Sold out</span>}
          </div>
          <div className="sf__pcard-action hidden md:block z-10">
            <button type="button" className={`sf__tooltip-item sf__btn-icon sf-wishlist__button sf__tooltip-left sf__tooltip-style-1 ${wished ? 'active' : ''}`} onClick={() => { toggleWish(p); toast(wished ? 'Removed from wishlist' : 'Added to wishlist'); }} aria-label="Add to wishlist">
              <span className="sf__tooltip-icon block">{wished ? <IconHeartFilled /> : <IconHeart />}</span>
              <span className="sf__tooltip-content sf-wishlist__button-content">{wished ? 'Remove from wishlist' : 'Add to wishlist'}</span>
            </button>
            <Link to={`/products/${p.slug}`} className="sf__tooltip-item sf__btn-icon sf-prod-compare__button sf__tooltip-left sf__tooltip-style-1 flex" aria-label="Quick view">
              <span className="sf__tooltip-icon block"><IconEye /></span>
              <span className="sf__tooltip-content sf-prod-compare__button-content">Quick view</span>
            </Link>
          </div>
          <div className="sf__pcard-quick-add">
            <button type="button" className="sf-pqv__button sf__btn sf__btn-secondary sf__btn-white flex-grow" onClick={quickAdd} disabled={soldOut}>
              <span>{soldOut ? 'Sold out' : hasOptions ? settings.shop?.quickAddText || 'Select options' : 'Add to cart'}</span>
            </button>
          </div>
        </div>
      </div>
      <div className={`sf__pcard-content ${listView ? 'text-left' : 'text-center'}`}>
        <div className="mt-3 lg:mt-5">
          <div className="max-w-full w-full">
            <h3 className="block text-base">
              <Link to={`/products/${p.slug}`} className="block mb-[5px] leading-normal sf__pcard-name font-medium truncate-lines hover:text-color-secondary">{p.title}</Link>
            </h3>
            {p.reviewCount > 0 && (
              <div className={`flex items-center ${listView ? '' : 'justify-center'} mb-1`}>
                <Stars value={p.rating || 5} size={12} />
                <span className="text-xs text-color-secondary ml-1">({p.reviewCount})</span>
              </div>
            )}
            <div className="sf__pcard-price leading-normal">
              <Price price={p.price} compareAtPrice={p.compareAtPrice} />
            </div>
            {optionValues.length > 0 && (
              <div className={`pcard-variant-option mt-3 flex flex-wrap items-center ${listView ? '' : 'justify-center'}`}>
                <div className="inline-flex flex-wrap">
                  {optionValues.map((v) => (
                    <Link key={v} to={`/products/${p.slug}`} className="product-option-item border border-color-border w-16 h-16 flex items-center justify-center mr-2 mb-2 text-xs" data-option-type="default">{v}</Link>
                  ))}
                  {rest > 0 && (
                    <Link to={`/products/${p.slug}`} className="sf-po__option-button rest-values sf-pqv__button">
                      <div className="sf__tooltip-item sf__tooltip-top">
                        <span className="text-base">+{rest}</span>
                        <span className="sf__tooltip-content">See {rest} more options</span>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            )}
            {listView && (
              <div className="content-view-list mt-5">
                <div className="leading-7 text-color-secondary mb-4">{p.shortDescription}</div>
                <div className="flex items-start">
                  <button type="button" className="sf-pqv__button quick-add sf__btn sf__btn-primary" onClick={quickAdd}><span>{hasOptions ? 'Select options' : 'Add to cart'}</span></button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
